/*
 * Beta features: switched on per company by a Super Admin (Admin, Beta features), and inherited
 * by an agency's client companies. Rows live in company_beta_features; this list is what the
 * server accepts and what the dashboard asks for.
 *
 * A feature graduates by being removed from this list. Its stored rows are then simply ignored
 * (knownBetaFeatures drops them), and its gate in the code is removed with it. That is why the
 * column is text validated against this list rather than a PostgreSQL enum: dropping an enum
 * value cannot be done by `db:push`, and graduating a feature should not need one.
 */
export const BETA_FEATURES = ['insights'] as const;

export type BetaFeature = (typeof BETA_FEATURES)[number];

/** The longest feature key the column holds. */
export const MAX_BETA_FEATURE_LENGTH = 64;

export const BETA_FEATURE_LABELS: Record<BetaFeature, string> = {
  insights: 'Insights',
};

export const BETA_FEATURE_DESCRIPTIONS: Record<BetaFeature, string> = {
  insights: 'Behaviour analysis of a website: funnels, journeys, pages and who visits, with segment comparison.',
};

export const isBetaFeature = (value: unknown): value is BetaFeature =>
  typeof value === 'string' && (BETA_FEATURES as readonly string[]).includes(value);

/**
 * The listed features among stored ones, once each, in the list's order. A stored row whose
 * feature has graduated (or was never listed) is dropped here, so nothing downstream sees it.
 */
export const knownBetaFeatures = (stored: readonly string[]): BetaFeature[] =>
  BETA_FEATURES.filter((feature) => stored.includes(feature));

/**
 * What /auth/me tells the dashboard about beta features. `betaFeatures` is the user's own
 * company's (with its agency's; every feature for a Super Admin). `clientBetaFeatures` is, for an
 * agency user, each client company they can scope to that has any, by company id (entries with
 * none are left out), so the dashboard answers for the client in scope as requireBeta does.
 */
export type BetaFeaturePayload = {
  betaFeatures: BetaFeature[];
  clientBetaFeatures: Record<number, BetaFeature[]>;
};

/**
 * Why a beta feature cannot be switched on for Pertento's own company: Super Admins already see
 * every one, and staff-created clients sit under Pertento, so the switch would reach managed
 * customers. The API refuses it with this and the admin picker shows it.
 */
export const PERTENTO_BETA_REFUSAL =
  "Not for Pertento's own company: Super Admins already see every beta feature, and switching Pertento on would reach the customers it manages.";
